import {
  baseballLogicService,
  Dexterity,
  HandednessFields,
  IApiBatter,
  IApiCatcher,
  IApiPitcher,
  IBulkPlayerEditorMetadata,
  IDataGridColumnConfiguration,
  IDataGridRow,
  IDataGridValidator,
  IPlayer,
  RightLeft,
  SVAppDefaultContextMenuOptionsBuilder,
} from '@trackman/baseball-shared-components';
import {
  MouseDownHandlerType,
  MouseEnterHandlerType,
} from '@trackman/baseball-shared-components/build/components/NewDataGrid/types/MouseEventHandlers';
import { IHighlightDisplayConfig } from '@trackman/baseball-shared-components/build/services/DataGrid/types/IHighlightDisplayConfig';
import NoData from 'components/NoData/NoData';
import { ServiceTypes } from 'constants/ServiceTypes';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ChangeTypes, RowAddChangeKeys, RowRemoveChangeKeys, UpdateChangeKeys } from 'enums/autosave/ChangeKeys';
import { DataKey } from 'enums/DataKey';
import { EditRowDataKey } from 'enums/EditRowDataKey';
import { TabsNames } from 'enums/TabsNames';
import mappingHelpers from 'helpers/mappingHelpers';
import { History } from 'history';
import { ISession } from 'interfaces/session/ISession';
import { IMergedPlayWithBallFinal } from 'interfaces/sessionVerification/plays/IPlayWithBallFinal';
import { observer } from 'mobx-react-lite';
import { PlayRow } from 'models/PlayEditRow/PlayRow';
import React, { useEffect } from 'react';
import { ClipboardService } from 'services/ClipboardService';
import { useInjection } from 'services/ioc/ioc.react';
import { notify } from 'services/NotificationService';
import { allValidators, basicValidators } from 'services/validators/AllValidators';
import { AutosaveStore } from 'stores/AutosaveStore';
import { ColumnsStore } from 'stores/ColumnsStore';
import { FilterStore } from 'stores/FilterStore';
import { SessionPlayStore } from 'stores/SessionPlayStore';
import { UiStore } from 'stores/UiStore';
import { PlaysGridRowManager } from '../../../../services/PlaysGridRowManager';
import ColumnsConfigurator from '../ColumnsConfigurator/ColumnConfigurator';
import Filters from '../Filters/Filters';
import BoxScore from './BoxScore/BoxScore';
import { AllColumns, BallTrackingColumns } from './EditPlaysColumnConfigurations';
import './EditPlaysGrid.css';
import SecondSourceBox from './SecondSourceBox/SecondSourceBox';
import { GridWrapper, PlayerHighlightButtons, PlayerHighlistBtn, StyledDataGrid } from './styles/EditPlaysGridStyles';
import { ValidationCell } from './ValidationCell/ValidationCell';

dayjs.extend(utc);

interface IProps {
  session: ISession;
  rowManager: PlaysGridRowManager;
  columns: IDataGridColumnConfiguration[];
  history: History;
}

export const EditPlaysGrid: React.FC<IProps> = observer((props): React.ReactElement => {
  const { session, rowManager, columns, history } = props;
  const sessionPlayStore = useInjection<SessionPlayStore>(ServiceTypes.SessionPlayStore);
  const columnsStore = useInjection<ColumnsStore>(ServiceTypes.ColumnsStore);
  const autosaveStore = useInjection<AutosaveStore>(ServiceTypes.AutosaveStore);
  const uiStore = useInjection<UiStore>(ServiceTypes.UiStore);
  const clipboardService = useInjection<ClipboardService<{}>>(ServiceTypes.ClipboardService);
  const filterStore = useInjection<FilterStore>(ServiceTypes.FilterStore);

  const addCreatedTimeToEmptyPlay = (apiPlay: IMergedPlayWithBallFinal) => {
    const d = dayjs().utc();

    if (rowManager.selection) {
      apiPlay.play.createdInStadiumUtc =
        rowManager.rows.find((row) => row.playId === rowManager.selection.rowId)?.createdInStadiumUtc || d.toISOString();
      apiPlay.play.createdInStadiumLocal =
        rowManager.rows.find((row) => row.playId === rowManager.selection.rowId)?.createdInStadiumLocal || d.local().format();
    } else {
      apiPlay.play.createdInStadiumUtc = d.toISOString();
      apiPlay.play.createdInStadiumLocal = d.local().format();
    }
  };

  const setupNullBaserunners = (apiPlay: IMergedPlayWithBallFinal) => {
    apiPlay.play.baseRunners = {
      firstBase: null,
      secondBase: null,
      thirdBase: null,
    };
  };

  const setupNullDefensivePlayers = (apiPlay: IMergedPlayWithBallFinal) => {
    apiPlay.play.players.firstBaseman = null;
    apiPlay.play.players.secondBaseman = null;
    apiPlay.play.players.thirdBaseman = null;
    apiPlay.play.players.centerFielder = null;
    apiPlay.play.players.rightFielder = null;
    apiPlay.play.players.leftFielder = null;
    apiPlay.play.players.shortStopper = null;
  };

  const setupDefaultValues = (apiPlay: IMergedPlayWithBallFinal) => {
    apiPlay.play.gameState.balls = 0;
    apiPlay.play.gameState.outs = 0;
    apiPlay.play.gameState.strikes = 0;
  };

  const createEmptyRow = (newRowId?: string) => {
    const apiPlay = {
      play: baseballLogicService.createEmptyApiPlay(session.id, newRowId),
      ballFinals: [],
      isCreated: true,
      isChanged: true,
      isBallTrackingChanged: true,
      isPlayerTrackingChanged: true,
      isMarkedForDeletion: false,
      changedValues: [],
    } as IMergedPlayWithBallFinal;

    addCreatedTimeToEmptyPlay(apiPlay);
    setupDefaultValues(apiPlay);

    if (session.isPlayerTrackingEnabled) {
      setupNullBaserunners(apiPlay);
      setupNullDefensivePlayers(apiPlay);
    }

    return new PlayRow(apiPlay, session.homeTeam, session.awayTeam);
  };

  const addRowAbove = () => {
    const newRow = createEmptyRow();
    newRow.trackStartTimeLocal = rowManager.rows[rowManager.findIndex(rowManager.selection.rowId)].trackStartTimeLocal;
    newRow.trackStartTimeUtc = rowManager.rows[rowManager.findIndex(rowManager.selection.rowId)].trackStartTimeUtc;
    autosaveStore.save(session.id, {
      [RowAddChangeKeys.Type]: ChangeTypes.AddAbove,
      [RowAddChangeKeys.NewRowId]: newRow.id,
      [RowAddChangeKeys.RowId]: rowManager.selection.rowId,
    });
    rowManager.selection && rowManager.insertRowAtPosition(newRow, rowManager.selection.rowId);

    rowManager.updateGrid();
  };
  const addRowBelow = () => {
    const newRow = createEmptyRow();
    newRow.trackStartTimeLocal = rowManager.rows[rowManager.findIndex(rowManager.selection.rowId)].trackStartTimeLocal;
    newRow.trackStartTimeUtc = rowManager.rows[rowManager.findIndex(rowManager.selection.rowId)].trackStartTimeUtc;
    autosaveStore.save(session.id, {
      [RowAddChangeKeys.Type]: ChangeTypes.AddBelow,
      [RowAddChangeKeys.NewRowId]: newRow.id,
      [RowAddChangeKeys.RowId]: rowManager.selection.rowId,
    });
    rowManager.selection && rowManager.insertRowAtPosition(newRow, rowManager.selection.rowId, true);

    rowManager.updateGrid();
  };
  const deleteRows = () => {
    for (let i = rowManager.selection.fromRowIndex; i <= rowManager.selection.toRowIndex; i++) {
      autosaveStore.save(session.id, {
        [RowRemoveChangeKeys.Type]: ChangeTypes.Remove,
        [RowRemoveChangeKeys.RowId]: rowManager.rows[i].id,
      });
    }

    rowManager.selection &&
      rowManager.deleteRows(rowManager.selection.rowId, rowManager.selection.toRowIndex - rowManager.selection.fromRowIndex + 1);

    rowManager.updateGrid();
  };

  const ignoreError = () => {
    rowManager.ignoreErrors(
      rowManager.columns[rowManager.selection.columnIndex].dataKey,
      rowManager.selection.fromRowIndex,
      rowManager.selection.toRowIndex - rowManager.selection.fromRowIndex + 1
    );
  };

  const setDexterityOnPlayerChange = (selectedRows: IDataGridRow[], value: any, column: any): void => {
    selectedRows.forEach((selectedRow) => {
      const row = rowManager.rows.find((row) => row.id === selectedRow.id);
      const pitchingHandedness = value.pitchingHandedness;
      const battingHandedness = value.battingHandedness;

      if (column.dataKey === DataKey.Pitcher) {
        if (pitchingHandedness === Dexterity.Both || pitchingHandedness === 'B') {
          if (row?.batterDexterity === Dexterity.Right || row?.batterDexterity === 'R') {
            rowManager.updateRow(selectedRow.displayIndex, DataKey.PitcherDexterity, Dexterity.Left, selectedRow.id);
          } else {
            rowManager.updateRow(selectedRow.displayIndex, DataKey.PitcherDexterity, Dexterity.Right, selectedRow.id);
          }
        } else {
          rowManager.updateRow(
            selectedRow.displayIndex,
            DataKey.PitcherDexterity,
            pitchingHandedness === 'R' || pitchingHandedness === Dexterity.Right ? Dexterity.Right : Dexterity.Left,
            selectedRow.id
          );
        }
      }

      if (column.dataKey === DataKey.Batter) {
        if (battingHandedness === Dexterity.Both || battingHandedness === 'B') {
          if (row?.pitcherDexterity === Dexterity.Right || row?.pitcherDexterity === 'R') {
            rowManager.updateRow(selectedRow.displayIndex, DataKey.BatterDexterity, Dexterity.Left, selectedRow.id);
          } else {
            rowManager.updateRow(selectedRow.displayIndex, DataKey.BatterDexterity, Dexterity.Right, selectedRow.id);
          }
        } else {
          rowManager.updateRow(
            selectedRow.displayIndex,
            DataKey.BatterDexterity,
            battingHandedness === 'R' || battingHandedness === Dexterity.Right ? Dexterity.Right : Dexterity.Left,
            selectedRow.id
          );
        }
      }

      rowManager.updateGrid();
    });
  };

  const applySavedChanges = (): void => {
    const { changes, sessionId } = autosaveStore.load();

    if (sessionId !== '' && session.id !== sessionId) {
      return notify('Autosave error', 'Current session id do not match with saved session id', 'error');
    }

    if (changes.length === 0) {
      return;
    }

    changes.forEach((change) => {
      switch (change.t) {
        case ChangeTypes.AddAbove: {
          const newRow = createEmptyRow(change[RowAddChangeKeys.NewRowId]);
          rowManager.insertRowAtPosition(newRow, change[RowAddChangeKeys.RowId]);
          break;
        }
        case ChangeTypes.AddBelow: {
          const newRow = createEmptyRow(change[RowAddChangeKeys.NewRowId]);
          rowManager.insertRowAtPosition(newRow, change[RowAddChangeKeys.RowId], true);
          break;
        }
        case ChangeTypes.Remove: {
          rowManager.deleteRows(change[RowRemoveChangeKeys.RowId], 1);
          break;
        }
        case ChangeTypes.Update: {
          const index = rowManager.findIndex(change[UpdateChangeKeys.RowId]);
          rowManager.updateRow(
            index,
            change[UpdateChangeKeys.DataKey],
            change[UpdateChangeKeys.NewValue],
            change[UpdateChangeKeys.RowId],
            false
          );

          if (
            change[UpdateChangeKeys.DataKey] === DataKey.Pitcher ||
            change[UpdateChangeKeys.DataKey] === DataKey.Batter ||
            change[UpdateChangeKeys.DataKey] === DataKey.Catcher
          ) {
            if (change[UpdateChangeKeys.NewValue]?.isPlaceholder) {
              addPlaceholderPlayerToFilters(change[UpdateChangeKeys.NewValue]);
            }
          }
          break;
        }
        default:
          break;
      }
    });

    notify('Autosave', 'Changes successfully restored', 'success');
  };

  const getValidators = (): IDataGridValidator[] => {
    if (session.isPlayerTrackingEnabled) {
      return allValidators;
    }

    return basicValidators;
  };

  const getColumns = (): IDataGridColumnConfiguration[] => {
    if (session.isPlayerTrackingEnabled) {
      return AllColumns;
    }

    return BallTrackingColumns;
  };

  const addPlaceholderPlayerToFilters = (player: IApiCatcher | IApiBatter | IApiPitcher) => {
    filterStore.addPlaceholderPlayerToFilters(mappingHelpers.apiPlayerToPlayerMap(player));
  };

  const pitcherAdditionalHandler = (selectedRows: IDataGridRow[], value: IApiPitcher, column: any) => {
    setDexterityOnPlayerChange(selectedRows, value, column);

    if (value.isPlaceholder) {
      addPlaceholderPlayerToFilters(value);
    }
  };

  const catcherAdditionalHandler = (selectedRows: IDataGridRow[], value: IApiCatcher, column: any) => {
    if (value.isPlaceholder) {
      addPlaceholderPlayerToFilters(value);
    }
  };

  const batterAdditionalHandler = (selectedRows: IDataGridRow[], value: IApiBatter, column: any) => {
    setDexterityOnPlayerChange(selectedRows, value, column);

    if (value.isPlaceholder) {
      addPlaceholderPlayerToFilters(value);
    }
  };

  useEffect(() => {
    if (sessionPlayStore.mergedPlays) {
      const rows = sessionPlayStore.mergedPlays.map((p) => new PlayRow(p, session.homeTeam, session.awayTeam));
      rowManager.setDefaultRows(rows);
      rowManager.setRows(rows);
      rowManager.setColumns(getColumns());

      applySavedChanges();

      rowManager.contextMenuOptionsBuilder = new SVAppDefaultContextMenuOptionsBuilder({
        addRowAbove,
        addRowBelow,
        deleteRows,
        ignoreError,
      });
      rowManager.getMetadataForBulkEditor = (col: IDataGridColumnConfiguration, rows: PlayRow[]) => {
        if (rows.length === 0) return;
        const isHomeTeamBatting = baseballLogicService.isHomeTeamBatting(rows[0].inning.isTopInning);
        switch (col.dataKey) {
          case EditRowDataKey.Pitcher:
            return {
              players: isHomeTeamBatting ? sessionPlayStore.awayTeamRoster : sessionPlayStore.homeTeamRoster,
              playerToOutputModelMap: (player: IPlayer | undefined) =>
                mappingHelpers.playerToEditRowDisplayPlayerMap(player, isHomeTeamBatting ? session.awayTeam.name : session.homeTeam.name),
              currentPlayer: rows.length === 1 ? rows[0][col.dataKey] : undefined,
              allowAddindForeignIdToPlaceholderPlayer: true,
              allowAddingPlaceholderPlayers: true,
              handednessFields: [HandednessFields.PITCHING],
              handednessOptions: RightLeft,
              additionalHandler: pitcherAdditionalHandler,
            } as IBulkPlayerEditorMetadata;
          case EditRowDataKey.Catcher:
            return {
              players: isHomeTeamBatting ? sessionPlayStore.awayTeamRoster : sessionPlayStore.homeTeamRoster,
              playerToOutputModelMap: (player: IPlayer | undefined) =>
                mappingHelpers.playerToEditRowDisplayPlayerMap(player, isHomeTeamBatting ? session.awayTeam.name : session.homeTeam.name),
              currentPlayer: rows.length === 1 ? rows[0][col.dataKey] : undefined,
              allowAddindForeignIdToPlaceholderPlayer: true,
              allowAddingPlaceholderPlayers: true,
              additionalHandler: catcherAdditionalHandler,
            } as IBulkPlayerEditorMetadata;
          case EditRowDataKey.CenterFielder:
          case EditRowDataKey.RightFielder:
          case EditRowDataKey.LeftFielder:
          case EditRowDataKey.ShortStopper:
          case EditRowDataKey.FirstBaseman:
          case EditRowDataKey.SecondBaseman:
          case EditRowDataKey.ThirdBaseman:
            return {
              players: isHomeTeamBatting ? sessionPlayStore.awayTeamRoster : sessionPlayStore.homeTeamRoster,
              playerToOutputModelMap: (player: IPlayer | undefined) =>
                mappingHelpers.playerToEditRowDisplayPlayerMap(player, isHomeTeamBatting ? session.awayTeam.name : session.homeTeam.name),
              currentPlayer: rows.length === 1 ? rows[0][col.dataKey] : undefined,
              allowAddindForeignIdToPlaceholderPlayer: true,
              allowAddingPlaceholderPlayers: true,
            } as IBulkPlayerEditorMetadata;
          case EditRowDataKey.FirstBase:
          case EditRowDataKey.SecondBase:
          case EditRowDataKey.ThirdBase:
            return {
              players: isHomeTeamBatting ? sessionPlayStore.homeTeamRoster : sessionPlayStore.awayTeamRoster,
              playerToOutputModelMap: (player: IPlayer | undefined) =>
                mappingHelpers.playerToEditRowDisplayPlayerMap(player, isHomeTeamBatting ? session.homeTeam.name : session.awayTeam.name),
              currentPlayer: rows.length === 1 ? rows[0][col.dataKey] : undefined,
              allowAddindForeignIdToPlaceholderPlayer: true,
              allowAddingPlaceholderPlayers: true,
            } as IBulkPlayerEditorMetadata;
          case EditRowDataKey.Batter:
            return {
              players: isHomeTeamBatting ? sessionPlayStore.homeTeamRoster : sessionPlayStore.awayTeamRoster,
              playerToOutputModelMap: (player: IPlayer | undefined) =>
                mappingHelpers.playerToEditRowDisplayPlayerMap(player, isHomeTeamBatting ? session.homeTeam.name : session.awayTeam.name),
              currentPlayer: rows.length === 1 ? rows[0][col.dataKey] : undefined,
              allowAddindForeignIdToPlaceholderPlayer: true,
              allowAddingPlaceholderPlayers: true,
              handednessFields: [HandednessFields.BATTING],
              handednessOptions: RightLeft,
              additionalHandler: batterAdditionalHandler,
            } as IBulkPlayerEditorMetadata;
        }
      };
      rowManager.boxScoreManager.highlightNavigationButtonRenderer = playerHighlightCustomElementRenderer;
      rowManager.validationService.validators = getValidators();
      rowManager.validateRows();
      rowManager.setLivePlays();
      rowManager.updateGrid();
    } else {
      rowManager.setRows([]);
    }
  }, [sessionPlayStore.mergedPlays]);

  useEffect(() => {
    rowManager.setColumns(
      columns.filter((column) => {
        return columnsStore.columns.find((savedColumn) => savedColumn.dataKey === column.dataKey && savedColumn.isChecked);
      })
    );
    rowManager.updateGrid();
  }, [columnsStore.shouldUpdate, columns]);

  useEffect(() => {
    rowManager._updateSelection(undefined);
  }, [uiStore.activeTab]);

  const stickyColumnCellRenderer = (
    key: string,
    row: PlayRow,
    rowIndex: number,
    style: any,
    onMouseDownHandler: MouseDownHandlerType,
    onMouseEnterHandler: MouseEnterHandlerType,
    isSelected?: boolean
  ) => {
    return (
      <ValidationCell
        isFirstPlayInInning={row.isFirstPlayInInning}
        key={key}
        style={style}
        cellContent={row.status}
        isHidden={!row.isVisible()}
        isSelected={isSelected}
        onMouseDownHandler={onMouseDownHandler}
        onMouseEnterHandler={onMouseEnterHandler}
        rowIndex={rowIndex}
        columnIndex={0}
        playSequenceNumber={row.playSequenceNumber}
      />
    );
  };

  const playerHighlightCustomElementRenderer = (config: IHighlightDisplayConfig) => {
    return (
      <PlayerHighlightButtons>
        <PlayerHighlistBtn
          onClick={(e) => {
            rowManager.scrollToNextHighlight(config, false);
            e.preventDefault();
          }}
        >
          Prev
        </PlayerHighlistBtn>
        <PlayerHighlistBtn
          onClick={(e) => {
            rowManager.scrollToNextHighlight(config, true);
            e.preventDefault();
          }}
        >
          Next
        </PlayerHighlistBtn>
      </PlayerHighlightButtons>
    );
  };

  useEffect(() => {
    rowManager.applyFilters(filterStore.filters);
    rowManager._updateSelection(undefined);

    if (rowManager.filters.length === 0) {
      rowManager.contextMenuOptionsBuilder = new SVAppDefaultContextMenuOptionsBuilder({
        addRowAbove,
        addRowBelow,
        deleteRows,
        ignoreError,
      });
    } else {
      rowManager.contextMenuOptionsBuilder = new SVAppDefaultContextMenuOptionsBuilder({
        ignoreError,
      });
    }
  }, [filterStore.filters]);

  return (
    <GridWrapper>
      {columnsStore.isConfiguratorVisible && <ColumnsConfigurator />}
      <Filters />
      {sessionPlayStore.mergedPlays.length > 0 ? (
        <StyledDataGrid
          rowManager={rowManager}
          stickyColumn={{
            width: 173,
            stickyMarginLeft: -49,
            cellRenderer: stickyColumnCellRenderer,
            stickyHeaderTitle: 'Pitch #',
          }}
          height='calc(100vh - 135px)'
          arrowsScrollControl
          clipboardManager={clipboardService}
        />
      ) : (
        <NoData history={history} />
      )}
      {uiStore.activeTab === TabsNames.BallTracking && <BoxScore rowManager={rowManager} session={session} />}
      {uiStore.activeTab === TabsNames.PlayerTracking && <SecondSourceBox rowManager={rowManager} session={session} />}
    </GridWrapper>
  );
});
